import React from "react";
import {
  Box,
  Divider,
  Flex,
  Heading,
  HStack,
  ListItem,
  Stack,
  Text,
  UnorderedList,
} from "@chakra-ui/layout";
import Link from "next/link";
import { Button } from "@chakra-ui/button";
import { useSession } from "next-auth/client";
import { AccountSwitcher } from "components/account";
import { useColorModeValue, useColorMode } from "@chakra-ui/color-mode";
import { FormControl, FormLabel } from "@chakra-ui/form-control";
import { Input } from "@chakra-ui/input";
import TimezoneSelect from "react-timezone-select";
import styled from "@emotion/styled";
import {
  Textarea, Switch, Tabs, TabList, TabPanels, Tab, TabPanel,
  theme,
  useMediaQuery,
  Tooltip,
  InputLeftAddon,
  InputGroup,
  InputRightElement,
  useDisclosure,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Tag,
} from "@chakra-ui/react";
import { firebaseClient, getToken } from "firebaseClient";
import toast from "react-hot-toast";
import { hslToHsla } from "../utils/helpers";
import SubscriptionManagement from "components/subscriptionManagement";
import { OrgManagement } from "components/subscriptionManagement/orgManagement";
import { CustomHead } from "components/custom-head";
import { updateEmail } from "../controllers/settings";
import * as analytics from "../utils/analytics";
import { useRouter } from "next/dist/client/router";
import { Navbar } from "components/navbar";
import ProtectedRoute from "components/protected-route";
import { QuestionIcon } from '@chakra-ui/icons';
import { Page } from "components/page";
import { getAccount, updateUser, getAuthUrl } from "../utils/sessionHelper";
import { ChevronDownIcon } from '@chakra-ui/icons';
import { RiCloseFill } from "react-icons/ri";
import { Icon } from "@chakra-ui/icons";
import { MoonIcon, SunIcon } from "@chakra-ui/icons";
import { DangerZone } from "components/danger-zone"
import { CustomSection } from "components/custom-section";
import { Integrations } from "components/settings/integrations";
import * as EmailValidator from 'email-validator';
import { useFirebaseUser } from "utils/useFirebaseUser";
import { AddAccount } from "components/popups/add-account";
import { formatDescription } from "../utils/tweetUtils";
import { MultiSelect, useMultiSelect } from "chakra-multiselect";
import { keywordOptions } from "utils/topicsList";
import { AuthCustom } from "components/auth-custom";
import { SaveIcon } from "components/icons/SaveIcon";
import { layerStyle, radius, textStyle } from "theme/names";
import { PlusIcon } from "components/icons/PlusIcon";
import { AILanguageOptions } from "utils/AILanguageOptions";
import { Select } from "chakra-react-select";
import { generateDescription } from "utils/staticStringGeneration";
import { getNMostRecentTweets, getTweet } from "controllers/search";
import { colors } from "theme/colors/colors";
import { SmallTweetGrid } from "components/tweets/smallTweetGrid";
import Loader from "react-spinners/PuffLoader";

let tabs = [
  "general",
  "integrations",
  "billing",
  "team",
]

const KeywordsMultiselect = ({ data, setData }) => {

  const { value, options, onChange } = useMultiSelect({
    value: data?.keywords,
    options: keywordOptions,
  });

  // console.log({data});
  // console.log(data?.length);


  return (
    <MultiSelect
      style={{ height: "60px", width: "500px" }}
      h={"12"}
      mt={2}
      options={options}
      {...{
        // select box
        controlProps: {
          maxWidth: "500px"
        },
        // dropdown list
        listProps: {},
        // typed text
        selectedListProps: {
          pl: 2,
          ml: 2,
        },
        // dropdown icon box
        actionGroupProps: {
          pr: 1,
        },
        value: data?.keywords,
        onChange: (values, actionAndValues) => {
          // remove punctuation characters from input
          values = (values as string[]).map((v) =>
            v.replace(/[^\w\s]/gi, "")
          );
          onChange && onChange(values, actionAndValues);
          setData({ ...data, keywords: values });
        },
      }}
      create
    />
  );
}

const LanguageSelect = ({ data, setData }) => {

  return (
    <Box w={"500px"}>
      <Select
        name={"languagePreferred"}
        onChange={(e) => {
          setData({ ...data, languagePreferred: e?.value });
        }}
        value={
          AILanguageOptions.filter(
            (option) => option.value === data?.languagePreferred
          )[0]
        }
        useBasicStyles
        options={AILanguageOptions}
        placeholder={"🇺🇸 English (US) (by default)"}
      />
    </Box>
  );
};

function LabelWithTooltip({ label, tooltipLabel }) {
  return (
    <FormLabel
      display={"flex"}
      alignItems={"center"}
      textStyle={textStyle["body.medium.standard"]}
      mt={3}
    >
      {label}
      <Tooltip
        hasArrow
        label={tooltipLabel}
      >
        <QuestionIcon color="gray.400" ml={2.5} />
      </Tooltip>
    </FormLabel>
  );
}

const Settings = ({ query }) => {
  const { colorMode, toggleColorMode } = useColorMode();
  const router = useRouter();
  const [session, loading] = useSession();
  const [isTooSmall] = useMediaQuery("(max-width: 600px)");
  const [selectedTimezone, setSelectedTimezone] = React.useState("");

  const [shareAccount, setShareAccount] = React.useState("");
  const [customPostUrl, setCustomPostUrl] = React.useState("");
  // const modes: Array<string> = ["editor (coming soon)", "full access"];
  const modes: Array<string> = ["editor", "full access"];
  const [selectedMode, setSelectedMode] = React.useState<String>(modes[1]);
  const [refresh, refresher] = React.useState({});
  const [tabId, setTabId] = React.useState(0);

  const [enableDailyEmail, setEnableDailyEmail] = React.useState(true);
  const [enableWeeklyEmail, setEnableWeeklyEmail] = React.useState(true);
  const [isRtl, setIsRtl] = React.useState(false);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [isUpdatingEmail, setIsUpdatingEmail] = React.useState(false);
  const [isUpdatingCustomPost, setIsUpdatingCustomPost] = React.useState(false);
  const [isUpdatingTimezone, setIsUpdatingTimezone] = React.useState(false);
  const [isUpdatingKeywords, setIsUpdatingKeywords] = React.useState(false);
  const [isUpdatingBio, setIsUpdatingBio] = React.useState(false);
  const [isUpdatingTopics, setIsUpdatingTopics] = React.useState(false);
  const [isUpdatingShare, setIsUpdatingShare] = React.useState(false);
  const [isUpdatingAI, setIsUpdatingAI] = React.useState(false);
  const [isUpdatingLanguagePreferred, setIsUpdatingLanguagePreferred] = React.useState(false);
  const [disableSponsorship, setDisableSponsorship] = React.useState(false);
  const [tempLevel, setTempLevel] = React.useState(3);
  const descriptionTextColor = useColorModeValue("gray.400", "gray.500");
  const searchSettingDescriptionTextColor = useColorModeValue("gray.400", "gray.500");
  const accountDescriptionTextColor = useColorModeValue("gray.400", "gray.500");
  const sharedAccountsContainerBg = useColorModeValue("gray.100", "gray.800");
  const closeIconColor = useColorModeValue("gray.600", "gray.100");
  const emailDescriptionTextColor = useColorModeValue("gray.400", "gray.500");
  const [data, setData] = React.useState({
    email: "",
    who: "",
    topics: "",
    keywords: [],
    languagePreferred: undefined,
  });
  const [hasAiCustomPosts, setHasAiCustomPosts] = React.useState(false);
  const [aiCustomPosts, setAiCustomPost] = React.useState<{ id: string, text: string }[]>([]);
  const isAuthenticated = useFirebaseUser();
  const { isOpen: isOpenConfirmAdd, onOpen: onOpenConfirmAdd, onClose: onCloseConfirmAdd } = useDisclosure();

  // console.log(getAccount(session)?.languagePreferred);
  // console.log(getAccount(session)?.description);


  const changeTabFromQuery = (query) => {
    if (query.tab) {
      let match = tabs.find(x => x == query.tab);
      if (match) {
        let index = tabs.indexOf(match);
        setTabId(index);
      }
    }
  }

  const isTwitterPostURL = (inputString) => {
    // Define the regex pattern for a Twitter/X post URL
    const twitterPostURLPattern = /https:\/\/(x\.com|twitter\.com)\/[A-Za-z0-9_]+\/status\/(\d+)/;

    // Use the exec() method to match and extract the post ID
    const match = twitterPostURLPattern.exec(inputString);

    // Check if there's a match and return the post ID
    if (match && match[2]) {
      return match[2];
    } else {
      return null; // Return null if no match is found
    }
  }

  const saveCustomPost = async () => {
    const postId = isTwitterPostURL(customPostUrl);
    if (!postId || !customPostUrl) {
      toast.error("please enter a valid post url");
      return;
    }

    setIsUpdatingCustomPost(true);
    const { tweet } = await getTweet(session, postId);
    const formatedTweet = {
      id: tweet.id,
      text: tweet.text ?? tweet.full_text,
      author_id: tweet.user?.id_str,
      profile_image_url: tweet.user?.profile_image_url,
      twUserName: tweet.user?.name,
    }
    let newState = [...aiCustomPosts];
    newState = [formatedTweet, ...newState.length >= 3 ? newState.slice(0, 2) : newState];
    getAccount(session).aiCustomPosts = newState;
    await updateUser(session, { aiCustomPosts: newState }, true, true);
    setAiCustomPost(newState);
    setIsUpdatingCustomPost(false);
    setCustomPostUrl("");
  }
  const extractBestLatest = async (session, twUserName) => {
    const results = await getNMostRecentTweets(session, { count: 99, idUser: twUserName });
    const filterdResults = results?.tweets?.filter((t) => !t.entities?.urls || t.entities?.urls?.every(url => Boolean(url.media_key)));

    if (!filterdResults || filterdResults.length === 0) return null;

    // Sort the tweets in descending order based on the sum of like_count and retweet_count (with retweet_count counting twice)
    const sortedTweets = filterdResults.sort((a, b) => (b.public_metrics?.like_count + (2 * b.public_metrics?.retweet_count)) - (a.public_metrics?.like_count + (2 * a.public_metrics?.retweet_count)));
    // Return the top 3 tweets
    return { tweets: sortedTweets.slice(0, 3), twitterUser: results.user };
  }
  React.useEffect(() => {
    const account = getAccount(session);
    if (!hasAiCustomPosts) {
      return;
    }
    if (hasAiCustomPosts && account.useSpecificPostForAi && account?.aiCustomPosts?.length) {
      setIsUpdatingCustomPost(false);
      return;
    }
    (async () => {
      const { tweets, twitterUser: user } = await extractBestLatest(session, account?.id) ?? { tweets: [], user: [] };
      const enrichedTweets = tweets.map(tweet => ({ ...tweet, profile_image_url: user[0]?.profile_image_url, twUserName: user[0]?.name }))
      if (enrichedTweets?.length) {
        await updateUser(session, { aiCustomPosts: enrichedTweets, useSpecificPostForAi: true }, true, true);
        setHasAiCustomPosts(true);
        setAiCustomPost(enrichedTweets);
        toast.success("AI settings updated");
      } else {
        setHasAiCustomPosts(false);
        toast.error("You dont't have any custom posts");
      }
      setIsUpdatingCustomPost(false);
    })()
  }, [hasAiCustomPosts])

  React.useEffect(() => {
    changeTabFromQuery(query);
  }, []);

  React.useEffect(() => {
    changeTabFromQuery(query);
  }, [query.tab]);

  React.useEffect(() => {
    if (getAccount(session)?.id) {
      setData({
        email: getAccount(session).email,
        who: getAccount(session).who,
        topics: getAccount(session).topics,
        keywords: getAccount(session)?.keywords,
        languagePreferred: getAccount(session)?.languagePreferred,
        // keywords: getAccount(session)?.keywords && getAccount(session)?.keywords?.join ? getAccount(session)?.keywords?.join(",") : "",
      });

      setSelectedTimezone(getAccount(session)?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone);
      setEnableWeeklyEmail(!getAccount(session)?.disableWeeklyDigest);
      setIsRtl(getAccount(session)?.isRtl);
      setEnableDailyEmail(!getAccount(session)?.disableDailyInspiration);
      setTempLevel(getAccount(session)?.tempLevel);
      setDisableSponsorship(getAccount(session)?.disableSponsorship);
      setHasAiCustomPosts(!!getAccount(session)?.aiCustomPosts?.length && getAccount(session)?.useSpecificPostForAi);
      setAiCustomPost(getAccount(session)?.aiCustomPosts);
    }
  }, [session]);

  if (!loading && !session) {
    return <ProtectedRoute />;
  }
  else if (!session) {
    return null;
  }

  const toggleAiCustomPosts = async () => {
    const newVal = !hasAiCustomPosts;
    if (!newVal) {
      await updateUser(session, { aiCustomPosts: [], useSpecificPostForAi: false }, true, true);
    } else {
      setIsUpdatingCustomPost(true);
    }
    setHasAiCustomPosts(newVal);
  }

  return (
    <Page title="Account settings" mainWrapperProps={{ margin: "unset", padding: "0" }}>
      <Box pl={isTooSmall ? 2 : 10}>
        {/* <Heading>Account settings</Heading> */}
        {/* <Divider mt={2} /> */}
        <Tabs
          mt={10}
          index={tabId}
          onChange={(index) => {
            setTabId(index);
            router.push({
              pathname: '/settings',
              query: { tab: tabs[index] }
            }, undefined, { shallow: false });
          }}
        >
          <TabList {...{
            maxW: ["100%", "none"],
            overflow: "scroll"
          }}>
            <Tab _focus={{ outline: "none" }}>General</Tab>
            <Tab _focus={{ outline: "none" }}>Integrations</Tab>
            <Tab _focus={{ outline: "none" }}>Billing</Tab>
            <Tab _focus={{ outline: "none" }}>Team & Permissions</Tab>
          </TabList>

          <TabPanels>
            <TabPanel mt={4}>
              <Stack spacing={4}>
                <CustomSection
                  title="Personal information"
                  description="Your main account information">
                  <Stack>
                    <Stack pl={[0, 4]}>
                      <FormControl id="email">
                        <FormLabel mt={5}>Email address</FormLabel>
                        <HStack>
                          <Input
                            value={data.email}
                            onChange={(e) =>
                              setData({ ...data, email: e.target.value })
                            }
                            maxW={250}
                            type="email"
                            placeholder="john@gmail.com"
                          />
                          <Button
                            isDisabled={getAccount(session)?.email === data.email}
                            isLoading={isUpdatingEmail}
                            onClick={async () => {

                              if (!data.email || !EmailValidator.validate(data.email)) {
                                toast.error("please enter valid email");
                                return;
                              }

                              setIsUpdatingEmail(true);
                              getAccount(session).email = data.email;
                              await updateEmail(getAccount(session).id, data.email);
                              // await mutate("/api/auth/session");
                              toast.success("Email updated", { style: { background: "gray.600", color: "#222" } });
                              setIsUpdatingEmail(false);
                            }}
                            leftIcon={SaveIcon}
                            variant={"primary"}
                          >
                            Save
                          </Button>
                        </HStack>
                      </FormControl>
                      <FormControl display="flex" flexDirection="column" mb={4}>
                        <FormLabel mt={3}>Timezone</FormLabel>
                        <HStack zIndex="9">
                          <StyledTimezone
                            className="blah"
                            colorMode={colorMode}
                            value={selectedTimezone}
                            onChange={(e: any) => { setSelectedTimezone(e.value) }}
                          />
                          <Button
                            isDisabled={getAccount(session)?.timezone && getAccount(session)?.timezone === selectedTimezone}
                            isLoading={isUpdatingTimezone}
                            onClick={async () => {
                              setIsUpdatingTimezone(true);
                              await updateUser(session, { timezone: selectedTimezone }, true, true);
                              setIsUpdatingTimezone(false);
                            }}
                            leftIcon={SaveIcon}
                            variant={"primary"}
                          >
                            Save
                          </Button>
                        </HStack>
                      </FormControl>
                      <FormControl display="flex" alignItems="center">
                        <FormLabel mt={3}>Dark Mode</FormLabel>
                        <Box pt={2}>
                          <SunIcon />
                          <Switch
                            ml={3}
                            mr={3}
                            isChecked={colorMode === "dark"}
                            onChange={toggleColorMode}
                          />
                          <MoonIcon />
                        </Box>
                      </FormControl>
                      <FormControl display="flex" alignItems="center" >
                        <FormLabel mt={3}>Right to left text direction</FormLabel>
                        <Box pt={2}>
                          <Switch
                            ml={3}
                            isChecked={isRtl}
                            onChange={async () => {
                              setIsRtl(!isRtl);
                              await updateUser(session, { isRtl: !isRtl }, true, true);
                            }}
                          />
                        </Box>
                      </FormControl>
                      {/* <FormControl display="flex" alignItems="center" >
                      <FormLabel mt={3}>Receive sponsorship opportunities</FormLabel>
                      <Box pt={2}>
                        <Switch
                          ml={3}
                          isChecked={!disableSponsorship}
                          onChange={async () => {
                            setDisableSponsorship(!disableSponsorship);
                            await updateUser(session, {disableSponsorship: !disableSponsorship}, true, true);
                          }}
                        />
                      </Box>
                    </FormControl> */}
                    </Stack>
                  </Stack>
                </CustomSection>
                <CustomSection
                  title="AI settings"
                  description="Edit your AI settings to help the AI generate better content for you">
                  <Stack id="search-settings">
                    <Stack pl={[0, 4]}>
                      <FormControl>
                        <LabelWithTooltip
                          {...{
                            label:
                              (getAccount(session)?.twUserName
                                ? "@" + getAccount(session)?.twUserName + "'s "
                                : "") + "preferred language",
                            tooltipLabel:
                              "Define the language you would like the AI to write in.",
                          }}
                        ></LabelWithTooltip>
                        <HStack>
                          <LanguageSelect
                            {...{
                              data,
                              setData,
                            }}
                          />
                          {
                            <Button
                              isDisabled={
                                JSON.stringify(
                                  getAccount(session)?.languagePreferred
                                ) === JSON.stringify(data.languagePreferred)
                              }
                              isLoading={isUpdatingLanguagePreferred}
                              variant="primary"
                              onClick={async () => {
                                setIsUpdatingLanguagePreferred(true);
                                let newLanguagePreferred = data.languagePreferred;
                                await updateUser(
                                  session,
                                  {
                                    languagePreferred: newLanguagePreferred,
                                    description: generateDescription(data.who, data.topics, data.languagePreferred),
                                  },
                                  true,
                                  true,
                                  {
                                    toastMessage: "Language updated"
                                  },
                                );
                                setIsUpdatingLanguagePreferred(false);
                              }}
                              leftIcon={SaveIcon}
                            >
                              Save
                            </Button>
                          }
                        </HStack>
                      </FormControl>
                      <FormControl>
                        <FormLabel mt={3}>
                          {(getAccount(session)?.twUserName ? "@" + getAccount(session)?.twUserName + "'s " : "") + "search keywords"}
                          <Tooltip hasArrow label="Define your main keywords to improve the auto search and the relevance of the daily emails">
                            <QuestionIcon color="gray.400" mt={-2} ml={4} />
                          </Tooltip>
                        </FormLabel>
                        <HStack>
                          <KeywordsMultiselect
                            {...{ data, setData }}
                          ></KeywordsMultiselect>
                          {
                            <Button
                              isDisabled={
                                JSON.stringify(
                                  getAccount(session)?.keywords
                                ) === JSON.stringify(data.keywords)
                              }
                              isLoading={isUpdatingKeywords}
                              onClick={async () => {
                                setIsUpdatingKeywords(true);
                                let newKeywords = data.keywords;
                                await updateUser(
                                  session,
                                  {
                                    keywords: newKeywords,
                                    description: generateDescription(data.who, data.topics, data.languagePreferred),
                                  },
                                  true,
                                  true
                                );
                                setIsUpdatingKeywords(false);
                              }}
                              leftIcon={SaveIcon}
                              variant={"primary"}
                            >
                              Save
                            </Button>
                          }
                        </HStack>
                      </FormControl>
                      <FormControl>
                        <FormLabel mt={3}>
                          {(getAccount(session)?.twUserName ? "@" + getAccount(session)?.twUserName + "'s " : "") + "personal description"}
                          <Tooltip hasArrow label="Tell us who you are to improve the results personalization">
                            <QuestionIcon color="gray.400" mt={-2} ml={4} />
                          </Tooltip>
                        </FormLabel>
                        <HStack>
                          <Textarea
                            value={data.who}
                            onChange={(e) => setData({ ...data, who: e.target.value })}
                            maxW={500}
                            placeholder="Maker"
                          />
                          {
                            <Button
                              isDisabled={getAccount(session)?.who === data.who}
                              isLoading={isUpdatingBio}
                              onClick={async () => {
                                if (!data.who || data.who?.length < 10 || data.who?.length > 300) {
                                  toast.error("please tell us who you are in 10 to 300 characters");
                                  return;
                                }
                                setIsUpdatingBio(true);
                                let description = formatDescription(data.who, getAccount(session)?.topics);
                                await updateUser(session,
                                  {
                                    who: data.who,
                                    description: generateDescription(data.who, data.topics, data.languagePreferred),
                                  }, true, true);
                                setIsUpdatingBio(false);
                              }}
                              leftIcon={SaveIcon}
                              variant={"primary"}
                            >
                              Save
                            </Button>
                          }
                        </HStack>
                      </FormControl>
                      <FormControl>
                        <FormLabel mt={3}>
                          {(getAccount(session)?.twUserName ? "@" + getAccount(session)?.twUserName + "'s " : "") + "main topics"}
                          <Tooltip hasArrow label="Tell us what you usually tweet about to improve the results personalization">
                            <QuestionIcon color="gray.400" mt={-2} ml={4} />
                          </Tooltip>
                        </FormLabel>
                        <HStack>
                          <Textarea
                            value={data.topics}
                            onChange={(e) =>
                              setData({ ...data, topics: e.target.value })
                            }
                            maxW={500}

                            placeholder="Building in public"
                          />
                          {
                            <Button
                              isDisabled={getAccount(session)?.topics === data.topics}
                              isLoading={isUpdatingTopics}
                              onClick={async () => {
                                if (!data.topics || data.topics?.length < 10 || data.topics?.length > 300) {
                                  toast.error("please tell us what you usually talk about in 10 to 300 characters");
                                  return;
                                }
                                setIsUpdatingTopics(true);
                                let description = formatDescription(getAccount(session)?.who, data.topics);
                                await updateUser(session, {
                                  topics: data.topics,
                                  description: generateDescription(data.who, data.topics, data.languagePreferred),
                                }, true, true);
                                setIsUpdatingTopics(false);
                              }}
                              leftIcon={SaveIcon}
                              variant={"primary"}
                            >
                              Save
                            </Button>
                          }
                        </HStack>
                      </FormControl>
                      <FormControl>
                        <FormLabel mt={3}>
                          {(getAccount(session)?.twUserName ? "@" + getAccount(session)?.twUserName + "'s " : "") + "writing style"}
                          <Tooltip hasArrow label="Define the writing style you want our AI to mimic, select 3 posts for that. By default we use your 3 latest posts">
                            <QuestionIcon color="gray.400" mt={-2} ml={4} />
                          </Tooltip>
                        </FormLabel>
                        <Box mt={4} display="flex" alignItems="flex-start">
                          Use custom posts as reference <Switch id="custom-post-style"
                            ml={3}
                            mr={3}
                            isChecked={hasAiCustomPosts}
                            onChange={toggleAiCustomPosts} />
                        </Box>
                        {hasAiCustomPosts && (
                          <Box
                            mt={3}
                            display={"flex"}
                          >
                            <Input
                              placeholder="Tweet url"
                              value={customPostUrl}
                              onChange={(e) => { setCustomPostUrl(e.target.value) }}
                              display={"flex"}
                              flexGrow={1}
                            />
                            <Button
                              display={"flex"}
                              flexGrow={0}
                              ml={3}
                              isLoading={isUpdatingEmail}
                              onClick={saveCustomPost}
                              leftIcon={SaveIcon}
                              variant={"primary"}
                            >
                              Save
                            </Button>
                          </Box>
                        )}
                        {(hasAiCustomPosts && !isUpdatingCustomPost) && (
                          <SmallTweetGrid aiCustomPosts={aiCustomPosts} session={session} colorMode={colorMode} setAiCustomPost={setAiCustomPost} />
                        )}
                        {isUpdatingCustomPost && (
                          <Box
                            width={"100%"}
                            mt={6}
                            display={"flex"}
                            alignItems={"center"}
                            justifyContent={"center"}
                          >
                            <Loader color={colorMode === "dark" ? hslToHsla(colors.primary.lightMode[100], 1) : hslToHsla(colors.primary.lightMode[400], 1)} />
                          </Box>
                        )}
                      </FormControl>
                      <FormControl>
                        <Box mt={4} display="flex" alignItems="flex-start">
                          <Text>
                            <b>Choose the freedom</b> you want to give to the AI to come up with new ideas.
                          </Text>
                          <Tooltip hasArrow label={"Conservative: will attempt to stay as close as possible to the original tweet with small variations. Standard: will attempt to stay close to the general idea of the tweet while offering more impactful variations. Wild: let the AI do its own thing, but you might get a few crazy guesses"}>
                            <QuestionIcon mt={1} color="gray.400" ml={4} />
                          </Tooltip>
                        </Box>
                        <Stack pl={4} maxW={500}>
                          <Flex w="100%" alignItems="center">
                            <Box w="300px">
                              <Flex fontWeight="600" justifyContent="space-between">
                                <Text mt={4}>Conservative</Text>
                                {/* <Text mt={4}>Standard</Text> */}
                                <Text mt={4}>Wild</Text>
                              </Flex>
                              <Box pl={6} pr={6}>
                                <Slider mt={5} css={{ marginTop: "-10px" }} width="100%" min={-2} max={2} step={1} variant={"primary"} aria-label="temp-selector" focusThumbOnChange={false}
                                  value={tempLevel} onChange={(val) => {
                                    setTempLevel(val);
                                  }}>
                                  <SliderTrack>
                                    <SliderFilledTrack />
                                  </SliderTrack>
                                  <SliderThumb />
                                </Slider>
                              </Box>
                            </Box>
                            <Button
                              isDisabled={getAccount(session)?.tempLevel === tempLevel}
                              isLoading={isUpdatingAI}
                              ml={10}
                              mt={2}
                              onClick={async () => {
                                setIsUpdatingAI(true);
                                // const db = firebaseClient.firestore();
                                // await db.collection("users").doc(getAccount(session).id).update({ tempLevel: tempLevel });
                                // getAccount(session).tempLevel = tempLevel;
                                // toast.success("AI settings updated");
                                await updateUser(session, { tempLevel: tempLevel }, true, true);
                                setIsUpdatingAI(false);
                              }}
                              leftIcon={SaveIcon}
                              variant="primary"
                            >
                              Save
                            </Button>
                          </Flex>
                        </Stack>
                      </FormControl>
                    </Stack>
                  </Stack>
                </CustomSection>
                {/* </Box> */}
                {/* <Box
                p={8}
                layerStyle={layerStyle["bg.border.rounded"]}
                borderRadius={radius.lg}
              > */}
                <CustomSection
                  title="Share your account"
                  description={<>
                    <Box maxW="800px">
                      Add people who will be authorized to tweet for you from Tweet Hunter. Note: that person needs to have a Tweet Hunter account as well, only your main main account can be shared.
                    </Box>
                    <UnorderedList>
                      <ListItem>
                        <Text maxW="800px">
                          Full access: full control over your account
                        </Text>
                      </ListItem>
                      <ListItem>
                        <Text maxW="800px">
                          Editor: can push content to the account owner for validation. Cannot schedule and publish tweets.
                        </Text>
                      </ListItem>
                    </UnorderedList>
                  </>}
                >

                  <Stack>
                    {/* <Heading mt={10} fontSize="lg">
                    Share your account
                  </Heading>
                  <Text color={accountDescriptionTextColor} maxW="800px">
                    Add people who will be authorized to tweet for you from Tweet Hunter. Note: that person needs to have a Tweet Hunter account as well, only your main main account can be shared.
                  </Text>
                  <Text color={accountDescriptionTextColor} maxW="800px">
                    Full access: full control over your account
                  </Text>
                  <Text color={accountDescriptionTextColor} maxW="800px">
                    Editor: can push content to the account owner for validation. Cannot schedule and publish tweets.
                  </Text> */}
                    {
                      getAccount(session)?.idAccount !== getAccount(session)?.id ? (
                        <Box>
                          <Tag mt={2} colorScheme="orange" size="lg">⚠️ secondary X accounts cannot be shared</Tag>
                        </Box>
                      ) : (
                        <Stack pl={[0, 4]}>
                          <FormControl>
                            <Flex mb={4} mt={4} flexWrap="wrap" alignItems="center" gap={4}
                              maxW={500}
                              justifyContent="flex-start">
                              <InputGroup size="md" >
                                <InputLeftAddon children="@" />
                                <Input placeholder="patxol" value={shareAccount} onChange={(e) => { setShareAccount(e.target.value) }} />
                              </InputGroup>
                              <Menu >
                                <MenuButton minW={150} size="md" variant={"secondary"} as={Button} rightIcon={<ChevronDownIcon />}>
                                  {selectedMode}
                                </MenuButton>
                                <MenuList >
                                  {
                                    modes.map((item, index) => (
                                      // <MenuItem isDisabled={item == modes[0]} onClick={() => setSelectedMode(item)} key={"menuSelectMore-" + index}>{item}</MenuItem>
                                      <MenuItem onClick={() => setSelectedMode(item)} key={"menuSelectMore-" + index}>{item}</MenuItem>
                                    ))
                                  }
                                </MenuList>
                              </Menu>
                              <Button
                                isDisabled={!(shareAccount?.length > 0)}
                                isLoading={isUpdatingShare}
                                onClick={async () => {
                                  setIsUpdatingShare(true);
                                  let dataToSend = {
                                    type: selectedMode,
                                    twUserName: shareAccount,
                                    mode: "add",
                                    idUser: getAccount(session).id,
                                    tokenUserId: session.user.uid,
                                    token: await getToken(session, "settings-share"),
                                  }
                                  let response = await fetch('/api/shareAccount', {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify(dataToSend),
                                  });
                                  let data = await response.json();
                                  if (data?.success && data.shareAccountsTo) {
                                    getAccount(session).shareAccountsTo = data.shareAccountsTo;
                                    setShareAccount("");
                                    refresher({});
                                    toast.success("Account shared successfully");
                                  }
                                  else {
                                    toast.error("Error: " + data.error);
                                  }
                                  setIsUpdatingShare(false);
                                }}
                                variant="primary"
                                leftIcon={PlusIcon}
                                px="4"
                              >
                                Add
                              </Button>
                            </Flex>
                          </FormControl>
                          {
                            getAccount(session)?.shareAccountsTo?.map((account, index) =>
                              <Flex maxW={500} key={"account-" + index} alignItems="center" justifyContent="space-between" m={4} p={2} borderRadius="6px" bg={sharedAccountsContainerBg}>
                                <Text ml={"10px"}>@{account.twUserName}</Text>
                                <Flex alignItems="center" justifyContent="flex-end">
                                  <Text mr={5}>{account.type}</Text>
                                  <IconButton
                                    aria-label="delete"
                                    color="twitter.600"
                                    // variant={"secondary"}
                                    size="sm"
                                    onClick={async () => {
                                      toast.promise(
                                        new Promise(async (resolve, reject) => {
                                          let dataToSend = {
                                            idUser: getAccount(session).id,
                                            type: account.type,
                                            twUserName: account.twUserName,
                                            mode: "delete",
                                            idGhost: account.id,
                                            tokenUserId: session.user.uid,
                                            token: await getToken(session, "settings-share-delete"),
                                          }
                                          let response = await fetch('/api/shareAccount', {
                                            method: 'POST',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify(dataToSend),
                                          });
                                          let data = await response.json();
                                          if (data?.success && data.shareAccountsTo) {
                                            getAccount(session).shareAccountsTo = data.shareAccountsTo;
                                            refresher({});
                                            resolve(data);
                                          }
                                          else {
                                            reject(data.error);
                                          }
                                        }),
                                        {
                                          loading: 'removing access to @' + account.twUserName,
                                          success: 'Access removed',
                                          error: 'An error happened',
                                        }
                                      );
                                    }}
                                    icon={
                                      <Icon color={closeIconColor} as={RiCloseFill} />
                                    }
                                  />
                                </Flex>
                              </Flex>
                            )
                          }
                        </Stack>
                      )
                    }
                  </Stack>
                </CustomSection>
                {/* </Box> */}
                {/* <Stack>
                  <Heading mt={10} fontSize="lg">
                    Email settings
                  </Heading>
                  <Text color={emailDescriptionTextColor}>
                    Enable / disable emails as you want
                  </Text>
                  <Stack pl={4}>
                    <FormControl>
                      <Box display="flex">
                        <FormLabel mt={3}>Daily inspiration email </FormLabel>
                        <Switch
                          isChecked={enableDailyEmail}
                          isDisabled={isUpdating}
                          m={4}
                          onChange={async () => {
                            setIsUpdating(true);
                            getAccount(session).disableDailyInspiration = enableDailyEmail;
                            const db = firebaseClient.firestore();
                            await db
                              .collection("users")
                              .doc(getAccount(session).id)
                              .update({ disableDailyInspiration: enableDailyEmail });
                            setEnableDailyEmail(!enableDailyEmail);
                            // await mutate("/api/auth/session");
                            postData({
                              url: "user-toggleDailyEmail",
                              token: "",
                              data: {
                                idUser: getAccount(session).id,
                                isDisable: enableDailyEmail,
                              },
                            });
                            setIsUpdating(false);
                          }}
                        />
                      </Box>
                    </FormControl>
                    <FormControl>
                      <Box display="flex">
                        <FormLabel mt={3}>Weekly analysis email</FormLabel>
                        <Switch
                          isChecked={enableWeeklyEmail}
                          isDisabled={isUpdating}
                          m={4}
                          onChange={async () => {
                            setIsUpdating(true);
                            getAccount(session).disableWeeklyDigest = enableWeeklyEmail;
                            const db = firebaseClient.firestore();
                            await db
                              .collection("users")
                              .doc(getAccount(session).id)
                              .update({ disableWeeklyDigest: enableWeeklyEmail });
                            setEnableWeeklyEmail(!enableWeeklyEmail);
                            // await mutate("/api/auth/session");
                            postData({
                              url: "user-toggleWeeklyEmail",
                              token: "",
                              data: {
                                idUser: getAccount(session).id,
                                isDisable: enableWeeklyEmail,
                              },
                            });
                            setIsUpdating(false);
                          }}
                        />
                      </Box>
                    </FormControl>
                  </Stack>
                </Stack> */}
                <CustomSection
                  title="X authorization"
                  description="If you feel anything is wrong, you can refresh your X token to make sure we can post for you"
                >
                  <Stack padding={4}>
                    <Button
                      variant={"primary"}
                      size="md"
                      width="max-content"
                      // as="a"
                      // href={getAuthUrl(session)}
                      // target="_blank"
                      onClick={() => {
                        if (session?.user?.uid == getAccount(session)?.idParent || session?.user?.uid == getAccount(session)?.id)
                          onOpenConfirmAdd();
                        else
                          toast.error("You can only do that from an account you own. Ask the owner of this account to do it.")
                      }}
                    >
                      Refresh my X Token
                    </Button>
                  </Stack>
                </CustomSection>
                {/* <CustomSection title="Custom Authentication" description="Setup via your API keys">
                  <Box maxW="500px" py="2">
                    <AuthCustom isInPopup={false} footerCss={{ justifyContent: "flex-start" }} />
                  </Box>
                </CustomSection> */}
                {/* <Box
                  p={8}
                  layerStyle={layerStyle["bg.border.rounded"]}
                  borderRadius={radius.lg}
                > */}
                {getAccount(session)?.id === session?.user?.uid && <DangerZone />}
                {/* </Box> */}
              </Stack>
            </TabPanel>
            <TabPanel>
              <Integrations />
            </TabPanel>
            <TabPanel>
              <Stack>
                {/* <MenuDivider /> */}
                {
                  //@ts-ignore
                  session?.user?.data?.subscription ? (
                    <Stack>
                      {
                        (!session?.user?.data?.subscription?.isInOrg
                          || session?.user?.data?.subscription?.orgRole == "Admin") ? (
                          <SubscriptionManagement session={session} />
                        ) : (
                          <Text textStyle={textStyle["body.medium.standard"]}>
                            You need to be an admin to manage subscriptions
                          </Text>
                        )
                      }
                    </Stack>
                  ) : (
                    <Box>
                      <Button
                        maxW="200px"
                        onClick={() => {
                          analytics.log("hit_unlock", { source: "menu" });
                          router.push("/pricing");
                        }}
                        // leftIcon={<FiUnlock />}
                        variant={"primary"}
                        css={{
                          width: "100%",
                        }}
                      >
                        Start Free Trial
                      </Button>
                    </Box>
                  )
                }
              </Stack>
            </TabPanel>
            <TabPanel>
              <Stack>
                <OrgManagement />
              </Stack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
      <AddAccount isOpen={isOpenConfirmAdd} onClose={onCloseConfirmAdd} />
    </Page>
  );
};

Settings.getInitialProps = async ({ query }) => {
  // console.log(query);
  return { query };
}


export default Settings;

export const StyledTimezone = styled(TimezoneSelect)`
  && {
    width: 250px;
    z-index: 100;

    > div {
      > div {
        > div {
          color: ${({ colorMode }: { colorMode: any }) =>
    colorMode === "light" ? `${theme.colors.gray[800]}` : `white`};
        }
      }
      font-size: 16px;
      background: ${({ colorMode }: { colorMode: any }) =>
    colorMode === "light" ? "white" : `${theme.colors.gray[800]}`};
      border: ${({ colorMode }: { colorMode: any }) =>
    colorMode === "light"
      ? `1px solid ${theme.colors.gray[300]}`
      : `1px solid ${theme.colors.gray[600]} `};
    }
    border: 0;
  }
`;
